'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';

import AlertItem from './components/AlertItem';
import type { AlertItemType } from './components/AlertItem';
import type { IconographyProps } from '../Iconography/Iconography';
import { defaultGlobalAlertManager } from './utils/createAlertManager';

import styles from './Alert.module.scss';

// Type definitions
type Theme = 'dark' | 'light';
type Variant = 'solid' | 'outlined';
type Position =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top-center'
  | 'bottom-center';
type Direction = 'ltr' | 'rtl';

interface AlertHeight {
  id: string;
  height: number;
}

interface AlertData {
  id: string;
  title?: string;
  description?: string;
  type?: AlertItemType;
  iconLeft?: IconographyProps;
  isDismissing?: boolean;
  gaText?: string;
  dismiss?: boolean;
  showDismiss?: boolean;
}

interface AlertManager {
  subscribe: (callback: (alert: unknown) => void) => () => void;
  dismiss: (id: string) => void;
  getAlerts: () => unknown[];
}

interface ClientOnlyPortalProps {
  children: React.ReactNode;
  selector?: string;
}

export interface AlertProps {
  theme?: Theme;
  variant?: Variant;
  position?: Position;
  className?: string;
  direction?: Direction;
  alertManager?: AlertManager;
  handleSingleDismiss?: (id: string) => void;
  handleAllDismissed?: () => void;
  dataTestId?: string;
  gaComponent?: string;
}

const gap = 16;

const ClientOnlyPortal: React.FC<ClientOnlyPortalProps> = (props) => {
  const ref = useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    ref.current = document.body;
    setMounted(true);
  }, [props.selector]);

  return mounted && ref.current ? createPortal(props.children, ref.current) : null;
};

const Alert: React.FC<AlertProps> = ({
  theme = 'dark',
  variant = 'solid',
  position = 'bottom-right',
  className,
  direction = 'ltr',
  alertManager = defaultGlobalAlertManager,
  handleSingleDismiss,
  handleAllDismissed,
  dataTestId = 'alert',
  gaComponent = 'Alert',
}) => {
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [heights, setHeights] = useState<AlertHeight[]>([]);
  const alertRefs = useRef<Record<string, HTMLElement | null>>({});

  // Function to update heights for each alert item
  const updateHeights = useCallback((id: string) => {
    const node = alertRefs.current[id];
    if (node) {
      const height = node.getBoundingClientRect().height;
      setHeights((currentHeights) => {
        // Find index of the height entry for the alert with this ID
        const existingIndex = currentHeights.findIndex((h) => h.id === id);

        if (existingIndex !== -1 && currentHeights[existingIndex].height !== height) {
          // Update height in place if the height has changed
          const updatedHeights = [...currentHeights];
          updatedHeights[existingIndex] = { id, height };
          return updatedHeights;
        } else if (existingIndex === -1) {
          // Add new entry if it doesn't exist
          return [...currentHeights, { id, height }];
        }

        // Return current heights if no update was needed
        return currentHeights;
      });
    }
  }, []);

  // Update all heights on window resize
  const updateAllHeights = useCallback(() => {
    alerts.forEach((alert) => {
      updateHeights(alert.id);
    });
  }, [alerts, updateHeights]);

  useEffect(() => {
    const handleResize = () => {
      updateAllHeights();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateAllHeights]);

  // Calculate the offset for each alert item based on heights
  const getOffset = (index: number): number => {
    return heights.reduce((total, { height }, i) => {
      if (i > index && !alerts[i]?.isDismissing) {
        // Only add height if the alert is after the current one and is not dismissing
        return total + height + gap;
      }
      return total;
    }, 0);
  };

  const performDismiss = useCallback(
    (id: string) => {
      setHeights((heights) => heights.filter((h) => h.id !== id)); // Remove height record
      delete alertRefs.current[id];
      handleSingleDismiss && handleSingleDismiss(id);
      if (alertManager.getAlerts().length === 0 && handleAllDismissed) {
        handleAllDismissed();
      }
    },
    [alertManager, handleSingleDismiss, handleAllDismissed]
  );

  const handleDismiss = (id: string) => {
    setAlerts((currentAlerts) =>
      currentAlerts.map((alert) => (alert.id === id ? { ...alert, isDismissing: true } : alert))
    );
    // dismiss after a short delay to allow for animation
    setTimeout(() => {
      alertManager.dismiss(id);
    }, 300); // Match this duration with CSS transition duration
  };

  useEffect(() => {
    const unsubscribe = alertManager.subscribe((alert: unknown) => {
      const alertData = alert as AlertData;
      setAlerts((currentAlerts) => {
        // If dismiss is true, remove the alert with the matching ID
        if (alertData.dismiss) {
          performDismiss(alertData.id);
          return currentAlerts.filter((a) => a.id !== alertData.id);
        }
        // Check if the alert ID already exists in the alerts array
        const alertExists = currentAlerts.some((a) => a.id === alertData.id);
        if (alertExists) {
          return currentAlerts; // Return the current alerts if the ID exists
        }
        return [...currentAlerts, alertData];
      });
    });

    return unsubscribe;
  }, [alertManager, performDismiss]);

  return (
    <ClientOnlyPortal>
      <ol
        className={classnames(styles.alertsContainer, styles[position])}
        aria-live="assertive"
        aria-atomic="true"
        data-testid={dataTestId}
      >
        {alerts.map((item, index) => {
          const offset = getOffset(index);
          return (
            <AlertItem
              ref={(node: HTMLElement | null) => {
                alertRefs.current[item.id] = node;
                if (node) {
                  updateHeights(item.id);
                }
              }}
              key={item.id}
              id={item.id}
              title={item.title}
              description={item.description}
              type={item.type}
              theme={theme}
              variant={variant}
              iconLeft={item.iconLeft}
              direction={direction}
              className={className}
              dataTestId={`${dataTestId}-${item.id}`}
              position={position}
              offset={offset}
              gap={gap}
              onDismiss={() => handleDismiss(item.id)}
              gaComponent={gaComponent}
              gaText={item.gaText}
              showDismiss={item.showDismiss ?? true}
            />
          );
        })}
      </ol>
    </ClientOnlyPortal>
  );
};

export default Alert;
